import { APP_DEFAULT_PATH } from 'config';
import useAuth from 'hooks/useAuth';
import useOrganization from 'hooks/useOrganization';
import { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { createSearchParams, useSearchParams } from 'react-router-dom';
import { GuardProps } from 'types/auth';

const PublicGuard = ({ children }: GuardProps) => {
  const urlParams = useParams();
  const { organization: currentOrganization, setCurrentOrganization } = useOrganization();
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const localStorageOrganizationId = window.localStorage.getItem('0paper-current-organization');

  async function getOrganizationByUrlOrLocalStorage() {
    let organizationId = urlParams.organizationId;

    if (organizationId === ':organizationId') {
      organizationId = undefined;
    }

    if (currentOrganization && currentOrganization?.id == organizationId) {
      return currentOrganization;
    }

    if (!organizationId) {
      if (!localStorageOrganizationId) {
        return 'select';
      }

      organizationId = localStorageOrganizationId;
    }

    if (organizationId && organizationId !== localStorageOrganizationId) {
      const organization = await setCurrentOrganization(organizationId);
      return organization;
    }

    if (location.pathname.includes(':organizationId')) {
      return 'replace';
    }

    if (organizationId === localStorageOrganizationId) {
      return 'scape';
    }
  }

  const init = async () => {
    const organizationReturned = await getOrganizationByUrlOrLocalStorage();

    if (organizationReturned === 'scape') {
      return navigateToWithSearchParams(window.location.pathname);
    }

    if (organizationReturned === 'select') {
      return navigate('/organization-select');
    }

    if (organizationReturned === 'replace') {
      return navigateToWithSearchParams(getOrganizationDashboardPath(localStorageOrganizationId || ''));
    }

    if (organizationReturned) {
      if (location.pathname === '/' || location.pathname === '/organization' || location.pathname === '/organization/:organizationId') {
        return navigateToWithSearchParams(getOrganizationDashboardPath(organizationReturned.id || ''));
      }

      if (isLoggedIn) {
        const pathStartsWithOrganization = location.pathname.startsWith('/organization');
        const path = pathStartsWithOrganization ? getLocationPathname(organizationReturned.id) : APP_DEFAULT_PATH;
        return navigateToWithSearchParams(path);
      }
    }
  };

  const navigateToWithSearchParams = (path: string) => {
    return navigate({
      pathname: path,
      search: createSearchParams(Object.fromEntries(searchParams)).toString()
    });
  };

  const getOrganizationDashboardPath = (organizationId: string) => {
    return `/organization/${organizationId}`;
  };

  const getLocationPathname = (organizationId?: string) => {
    return replaceOrganizationIdInPathname(location.pathname, organizationId || '');
  };

  const replaceOrganizationIdInPathname = (pathname: string, organizationId: string) => {
    return pathname.includes(':organizationId') ? pathname.replace(':organizationId', organizationId) : pathname;
  };

  useEffect(() => {
    init();
  }, []);

  return children;
};

export default PublicGuard;
